import React from 'react';
import {
    IFloatingToolbarGroup,
    GROUP_DIVIDER,
    IFloatingToolbarItem,
    IClickItemInfo,
    ICustomSubmenuProps
} from '@techsee/techsee-ui-common/lib/floating-toolbar/contracts';
import {ITranslate} from '../../../_react_/services/LocalizationService';
import {GuidancePopoversController, IGuidancePopoversController} from './guidance-popovers/GuidancePopovers.controller';
import {IDomUtilsService} from '@techsee/techsee-client-infra/lib/services/DomUtilsService';
import {SpeedTestGuidance} from './guidance-popovers/SpeedTestGuidance';
import {FlashlightGuidance} from './guidance-popovers/FlashlightGuidance';
import get from 'lodash/get';
import RangeSlider from '@techsee/techsee-ui-common/lib/floating-toolbar/components/RangeSlider';
import ColorPicker from '@techsee/techsee-ui-common/lib/floating-toolbar/components/ColorPicker';
import TagsSelector from '@techsee/techsee-ui-common/lib/floating-toolbar/components/TagsSelector';
import {ISpeedtestService} from '../../../_react_/services/SpeedtestService';
import {IRoomService} from '@techsee/techsee-client-infra/lib/services/RoomService';
import {IEventLogsService} from '../../../_react_/services/EventsLogService';
import {LOG_EVENTS} from '@techsee/techsee-common/lib/constants/event-logs.constants';
import {RECORDING_STATUS} from '@techsee/techsee-common/lib/constants/room.constants';
import {CONNECTION_STATUS} from '../../../_react_/services/ts-locale-helper/ts-locale-helper.settings';
import {ComponentBaseController} from '@techsee/techsee-client-infra/lib/infra/ComponentBaseController';
import {computed} from 'mobx';
import {IFloatingToolbarServices} from './ts-floating-toolbar.controller';

export enum CANVAS_TOOLS {
    HAND = 'hand',
    RECTANGLE = 'rectangle',
    CIRCLE = 'circle',
    ARROW = 'arrow',
    PEN = 'pen',
    TEXT = 'text',
    NUMTAG = 'numtag',
    TEXTTAG = 'texttag',
    ANALYZE = 'analyze'
}

export interface IToolbarOptionsController {
    readonly toolbarOptions: IFloatingToolbarGroup[];
}

export interface ToolHandler {
    activate: () => void;
    deactivate: () => void;
    updateIcon?: (iconName: string) => void;
}

export enum TOOL_HANDLER_KEYS {
    MOVE = 'move',
    SHAPE = 'shape',
    TEXT = 'text',
    LABELS = 'labels',
    ZOOM = 'zoom'
}

export enum ToolbarEventType {
    desktop_sharing_toggle_control = 'desktop_sharing_toggle_control',
    send_image = 'send_image',
    save_resource = 'save_resource',
    send_flashlight_guidance = 'send_flashlight_guidance',
    scan = 'scan',
    set_flashlight_states = 'set_flashlight_states',
    set_toolbar_scan_states = 'set_toolbar_scan_states',
    copy_to_clipboard = 'copy_to_clipboard',
    download = 'download',
    email = 'email',
    run_speedtest = 'run_speedtest',
    record_button = 'record_button',
    run_wifi_scan = 'run_wifi_scan',
    start_wifi_visual_scan = 'start_wifi_visual_scan'
}

export class ToolbarOptionsController extends ComponentBaseController implements IToolbarOptionsController {
    private _domUtilsService: IDomUtilsService;

    private _tsCanvasAnnotate: any;

    private _translate: ITranslate;

    private _speedtestService: ISpeedtestService;

    private _roomService: IRoomService;

    private _eventsLogService: IEventLogsService;

    private _guidancePopoversController: IGuidancePopoversController;

    private _canvasToolsHandlers: Record<TOOL_HANDLER_KEYS, ToolHandler>;

    private _lastShape: {tool: CANVAS_TOOLS; icon: string} = {tool: CANVAS_TOOLS.ARROW, icon: 'arrow'};

    constructor(
        private _accountData: any,
        private _dashboardState: any,
        private services: IFloatingToolbarServices
    ) {
        super();

        this._domUtilsService = services.domUtilsService;
        this._translate = services.localizationService.translate;
        this._speedtestService = services.speedtestService;
        this._roomService = services.roomService;
        this._eventsLogService = services.eventsLogService;
        this._tsCanvasAnnotate = services.tsCanvasAnnotate;

        this._guidancePopoversController = new GuidancePopoversController(
            this._translate,
            this._domUtilsService,
            this._speedtestService,
            get(this._accountData, 'protectedSettings.mobileSpeedtest.ooklaPremium'),
            get(this._accountData, 'protectedSettings.mobileSpeedtest.defaultProvider')
        );

        this._canvasToolsHandlers = {
            move: {activate: () => {}, deactivate: () => {}},
            shape: {activate: () => {}, deactivate: () => {}},
            text: {activate: () => {}, deactivate: () => {}},
            labels: {activate: () => {}, deactivate: () => {}},
            zoom: {activate: () => {}, deactivate: () => {}}
        };
    }

    private _shareDisabledByAllToggles(): boolean {
        const enableCopyToClipboard = get(this._accountData, 'protectedSettings.enableCopyToClipboard');
        const enableEmailImageSharing = get(this._accountData, 'protectedSettings.emailImageSharing');
        const enableLocalImageSaving = get(this._accountData, 'protectedSettings.localImageSaving');

        return !enableCopyToClipboard && !enableEmailImageSharing && !enableLocalImageSaving;
    }

    @computed
    get toolbarOptions(): IFloatingToolbarGroup[] {
        const groups: IFloatingToolbarGroup[] = [
            // Group 1
            {
                groupId: 'Group1',
                available: this._dashboardState.floatingToolbarState.isDesktopSharingRemoteControlSession,
                items: [
                    {
                        title: this._dashboardState.clientRoomStorage.agentHasRemoteControl
                            ? this._translate('REACT.FLOATING_TOOLBAR.DS_STOP_CONTROL')
                            : this._translate('REACT.FLOATING_TOOLBAR.DS_REQUEST_CONTROL'),
                        icon: 'control',
                        disabled: !this._dashboardState.livePointerRoomStorage.connected,
                        iconClassName: `icon-ds-control-${this._dashboardState.clientRoomStorage.agentHasRemoteControl ? 'active' : 'inactive'}`,
                        clickHandler: () => {
                            this.emit(ToolbarEventType.desktop_sharing_toggle_control);
                        }
                    }
                ]
            },
            // Group 2
            {
                groupId: 'Group2',
                available: this._dashboardState.hideWifiScan,
                items: [
                    {
                        title: this._translate('REACT.FLOATING_TOOLBAR.SAVE_CHANGES'),
                        icon: 'save',
                        available: this._dashboardState.floatingToolbarState.displaySaveButton,
                        disabled: this._dashboardState.floatingToolbarState.disabledSaveButton,
                        clickHandler: () => {
                            this.emit(ToolbarEventType.save_resource);
                            this._setDefaultTool();
                        }
                    }
                ]
            },
            // Group 3
            {
                groupId: 'Group3',
                available:
                    this._dashboardState.isEnableWifiScan ||
                    this._dashboardState.isEnableVisualScan ||
                    (get(this._accountData, 'protectedSettings.imageAnalysis', false) &&
                        this._dashboardState.isToggleEnabled_ScanCaptureDesign),
                items: [
                    {
                        icon: 'wifi-find-outlined',
                        title: this._translate('REACT.FLOATING_TOOLBAR.WIFI_SCAN'),
                        available: this._dashboardState.isEnableWifiScan,
                        clickHandler: () => this._wifiScanClickHandler(),
                        disabled: this._isWifiScanButtonDisabled,
                        isTooltipVisible: true,
                        tooltipContent: this.getWifiScanTooltipContent
                    },
                    {
                        icon: 'view-in-ar-outlined',
                        title: this._dashboardState.floatingToolbarState.isStopVisualWifiScan
                            ? this._translate('REACT.FLOATING_TOOLBAR.STOP_WIFI_VISUAL_SCAN')
                            : this._translate('REACT.FLOATING_TOOLBAR.WIFI_VISUAL_SCAN'),
                        available: this._dashboardState.isEnableVisualScan,
                        clickHandler: () => this._wifiVisualScanClickHandler(),
                        isTooltipVisible: true,
                        tooltipContent: this.getVisualScanTooltipContent,
                        disabled: this._dashboardState.floatingToolbarState.isRunWifiVisualScanButtonDisabled,
                        iconClassName: this._dashboardState.floatingToolbarState.isStopVisualWifiScan
                            ? 'stop-visual-scan'
                            : ''
                    },
                    {
                        icon: 'scan',
                        title: this._translate('REACT.FLOATING_TOOLBAR.OCR_SCAN'),
                        available:
                            this._dashboardState.hideWifiScan &&
                            get(this._accountData, 'protectedSettings.imageAnalysis', false) &&
                            this._dashboardState.isToggleEnabled_ScanCaptureDesign,
                        disabled: this._isOCRScanButtonDisabled,
                        activatable: true,
                        isTooltipVisible: true,
                        tooltipContent: this.getOCRScanTooltipContent,
                        clickHandler: () => {
                            this.emit(ToolbarEventType.scan);

                            this._setDefaultTool();
                        },
                        setItemHandlers: ({activateItem, deactivateItem}) => {
                            this.emit(ToolbarEventType.set_toolbar_scan_states, {activateItem, deactivateItem});
                        }
                    }
                ]
            },
            // Group 4
            {
                groupId: 'Group4',
                available: this._dashboardState.hideWifiScan,
                items: [
                    {
                        icon: 'sendnow',
                        title: this._translate('REACT.FLOATING_TOOLBAR.SEND'),
                        available: true,
                        disabled:
                            !(
                                !this._dashboardState.isObserve &&
                                (this._dashboardState.connectionStatus !== CONNECTION_STATUS.WAITING ||
                                    this._dashboardState.isOfflineRoom) &&
                                this._dashboardState.canvasReady
                            ) ||
                            (this._dashboardState.displayLoaderReconnecting &&
                                get(this._accountData, 'protectedSettings.enableIdleModeUsability')),
                        clickHandler: () => {
                            this.emit(ToolbarEventType.send_image, () => {
                                this._setDefaultTool();
                            });
                        }
                    },
                    {
                        icon: 'sharing',
                        title: this._translate('REACT.FLOATING_TOOLBAR.SHARE'),
                        available:
                            get(this._accountData, 'protectedSettings.enableShareMenu') &&
                            !get(this._accountData, 'protectedSettings.quickSharingModesEnabled') &&
                            !this._shareDisabledByAllToggles(),
                        disabled: !(
                            (this._dashboardState.videoModeState.preloadedImage.resource &&
                                this._dashboardState.offlineMode &&
                                !this._dashboardState.isLibraryTaggingMode) ||
                            (!this._dashboardState.isObserve &&
                                this._dashboardState.connectionStatus !== CONNECTION_STATUS.WAITING &&
                                this._dashboardState.canvasReady)
                        ),
                        children: [
                            {
                                title: this._translate('REACT.FLOATING_TOOLBAR.SHARE.COPY_TO_CLIPBOARD'),
                                icon: 'copytoclipboard',
                                available: get(this._accountData, 'protectedSettings.enableCopyToClipboard'),
                                clickHandler: () => {
                                    this.emit(ToolbarEventType.copy_to_clipboard);
                                    this._setDefaultTool();
                                }
                            },
                            {
                                title: this._translate('REACT.FLOATING_TOOLBAR.SHARE.DOWNLOAD'),
                                icon: 'download',
                                available: get(this._accountData, 'protectedSettings.localImageSaving'),
                                clickHandler: () => {
                                    this.emit(ToolbarEventType.download);
                                    this._setDefaultTool();
                                }
                            },
                            {
                                title: this._translate('REACT.FLOATING_TOOLBAR.SHARE.EMAIL'),
                                icon: 'sendsms',
                                available: get(this._accountData, 'protectedSettings.emailImageSharing'),
                                clickHandler: () => {
                                    this.emit(ToolbarEventType.email);
                                    this._setDefaultTool();
                                }
                            }
                        ]
                    }
                ]
            },
            // Group 5
            {
                groupId: 'Group5',
                available:
                    (this._dashboardState.floatingToolbarState.displayFloatingToolbarSpeedtest ||
                        this._dashboardState.floatingToolbarState.displayFloatingToolbarFlashlight) &&
                    this._dashboardState.hideWifiScan,
                items: [
                    {
                        icon: 'speedtest',
                        title: this._translate('REACT.FLOATING_TOOLBAR.SPEEDTEST'),
                        available:
                            this._dashboardState.floatingToolbarState.displayFloatingToolbarSpeedtest &&
                            this._dashboardState.hideWifiScan,
                        disabled: this._dashboardState.floatingToolbarState.disabledSpeedtestButton(
                            !!this._guidancePopoversController.speedtestResults
                        ),
                        popover: {
                            title: this._guidancePopoversController.speedtestResults
                                ? this._translate('REACT.FLOATING_TOOLBAR.SPEEDTEST_RESULTS_TITLE')
                                : this._translate('REACT.FLOATING_TOOLBAR.SPEEDTEST_GUIDANCE_TITLE'),
                            content: () => <SpeedTestGuidance ctrl={this._guidancePopoversController} />,
                            closeOnBodyClick: true,
                            toggleOnClick: true,
                            deactivateOnClose: true
                        },
                        setItemHandlers: ({setPopover}) => {
                            this._guidancePopoversController.speedtestPopoverActivate = setPopover;
                        },
                        clickHandler: ({activateItem, deactivateItem, isActive}) =>
                            this._speedtestClickHandler(activateItem, deactivateItem, isActive)
                    },
                    {
                        icon: 'no-torch',
                        activeIcon: 'torch',
                        title: this._translate('REACT.FLOATING_TOOLBAR.FLASHLIGHT'),
                        available:
                            this._dashboardState.floatingToolbarState.displayFloatingToolbarFlashlight &&
                            !this._dashboardState.isObserve &&
                            this._dashboardState.hideWifiScan,
                        disabled:
                            !this._dashboardState.floatingToolbarState.isFlashlightAvailableOnClient ||
                            this._dashboardState.displayLoaderReconnecting,
                        activatable: true,
                        popover: {
                            title: this._translate('REACT.FLOATING_TOOLBAR.FLASHLIGHT_GUIDANCE_TITLE'),
                            content: (properties: {isActive: boolean}) => (
                                <FlashlightGuidance
                                    ctrl={this._guidancePopoversController}
                                    isTurnedOn={properties.isActive}
                                    onSend={() => this.emit(ToolbarEventType.send_flashlight_guidance)}
                                />
                            ),
                            toggleOnClick: true,
                            deactivateOnClose: false,
                            showDots: true
                        },
                        setItemHandlers: ({activateItem, deactivateItem}) => {
                            this.emit(ToolbarEventType.set_flashlight_states, {activateItem, deactivateItem});
                        },
                        clickHandler: () => {
                            this._setDefaultTool();
                        }
                    }
                ]
            },
            // Group 6
            {
                groupId: 'Group6',
                available: this._dashboardState.hideWifiScan,
                items: [
                    {
                        title: this.recordingStatusMessage,
                        icon: 'recodring',
                        available:
                            this._dashboardState.floatingToolbarState.isRecordingEnabled &&
                            !get(this._dashboardState, 'accountData.protectedSettings.recording.automatic.enabled'),
                        disabled: !this._dashboardState.floatingToolbarState.recordButtonReady,
                        toggleOnClick: true,
                        activatable: true,
                        iconClassName: `icon-recording-${this._dashboardState.dashboardRoomStorage.recordingStatus}`,
                        clickHandler: () => this.emit(ToolbarEventType.record_button)
                    }
                ]
            },
            // Group 7
            {
                groupId: 'Group7',
                minified: true,
                divider: GROUP_DIVIDER.THIN,
                available:
                    !this._dashboardState.isObserve &&
                    (this._dashboardState.isCanvas || this._dashboardState.isImageScan) &&
                    this._dashboardState.hideWifiScan,
                items: [
                    {
                        title: this._translate('REACT.FLOATING_TOOLBAR.ZOOM'),
                        icon: 'zoom',
                        showChildrenOnClick: true,
                        disabled: this._isZoomDisabled(),
                        activatable: true,
                        setItemHandlers: ({activateItem, deactivateItem}) => {
                            this._canvasToolsHandlers.zoom = {
                                activate: activateItem,
                                deactivate: deactivateItem
                            };
                        },
                        customSubmenu: () => {
                            const zoomValue = this._tsCanvasAnnotate.getZoom();

                            return (
                                <RangeSlider value={zoomValue} onChange={(value) => this._zoomChanged(Number(value))} />
                            );
                        }
                    },
                    {
                        title: this._translate('REACT.FLOATING_TOOLBAR.MOVE'),
                        icon: 'pan',
                        toggleOnClick: true,
                        disabled: this._isMoveDisabled(),
                        activatable: true,
                        setItemHandlers: ({activateItem, deactivateItem}) => {
                            this._canvasToolsHandlers.move = {
                                activate: activateItem,
                                deactivate: deactivateItem
                            };
                        },
                        clickHandler: ({isActive}) => {
                            this._moveClick(isActive);
                        }
                    },
                    {
                        title: this._translate('REACT.FLOATING_TOOLBAR.ROTATE_LEFT'),
                        icon: 'rotate',
                        iconClassName: 'flipped',
                        disabled: this._isRotateDisabled(),
                        clickHandler: () => {
                            this._tsCanvasAnnotate.rotateLeft();
                        }
                    },
                    {
                        title: this._translate('REACT.FLOATING_TOOLBAR.ROTATE_RIGHT'),
                        icon: 'rotate',
                        disabled: this._isRotateDisabled(),
                        clickHandler: () => {
                            this._tsCanvasAnnotate.rotateRight();
                        }
                    },
                    {
                        title: this._translate('REACT.FLOATING_TOOLBAR.COLOR'),
                        icon: 'color',
                        showChildrenOnClick: true,
                        disabled: this._isDrawDisabled(),
                        setItemHandlers: ({updateStyles}) => {
                            updateStyles({
                                icon: {color: this._tsCanvasAnnotate.getColor()}
                            });
                        },
                        customSubmenu: (props) => (
                            <ColorPicker onSelect={(event, color) => this._updateColor(color, props)} />
                        )
                    },
                    {
                        title: this._translate('REACT.FLOATING_TOOLBAR.SHAPE'),
                        icon: 'shape',
                        showChildrenOnClick: true,
                        disabled: this._isDrawDisabled(),
                        activatable: true,
                        setItemHandlers: ({activateItem, deactivateItem, updateIcon}) => {
                            this._canvasToolsHandlers.shape = {
                                activate: activateItem,
                                deactivate: deactivateItem,
                                updateIcon: updateIcon
                            };

                            this._deactivateTools();
                            activateItem();
                            updateIcon('arrow');

                            this._tsCanvasAnnotate.setTool(CANVAS_TOOLS.ARROW);
                        },
                        children: [
                            {
                                title: this._translate('REACT.FLOATING_TOOLBAR.SHAPE_RECTANGLE'),
                                icon: 'rectangle',
                                clickHandler: (item) => {
                                    this._shapeClick(CANVAS_TOOLS.RECTANGLE, item);
                                }
                            },
                            {
                                title: this._translate('REACT.FLOATING_TOOLBAR.SHAPE_CIRCLE'),
                                icon: 'circle',
                                clickHandler: (item) => {
                                    this._shapeClick(CANVAS_TOOLS.CIRCLE, item);
                                }
                            },
                            {
                                title: this._translate('REACT.FLOATING_TOOLBAR.SHAPE_ARROW'),
                                icon: 'arrow',
                                clickHandler: (item) => {
                                    this._shapeClick(CANVAS_TOOLS.ARROW, item);
                                }
                            },
                            {
                                title: this._translate('REACT.FLOATING_TOOLBAR.SHAPE_PEN'),
                                icon: 'freehand',
                                clickHandler: (item) => {
                                    this._shapeClick(CANVAS_TOOLS.PEN, item);
                                }
                            }
                        ]
                    },
                    {
                        title: this._translate('REACT.FLOATING_TOOLBAR.TEXT'),
                        icon: 'text',
                        activatable: true,
                        disabled: this._isDrawDisabled(),
                        setItemHandlers: ({activateItem, deactivateItem}) => {
                            this._canvasToolsHandlers.text = {
                                activate: activateItem,
                                deactivate: deactivateItem
                            };
                        },
                        clickHandler: ({isActive}) => {
                            this._textClick(isActive);
                        }
                    },
                    {
                        title: this._translate('REACT.FLOATING_TOOLBAR.TAGS'),
                        icon: 'templates',
                        disabled: this._isDrawDisabled(),
                        showChildrenOnClick: true,
                        activatable: true,
                        setItemHandlers: ({activateItem, deactivateItem}) => {
                            this._canvasToolsHandlers.labels = {
                                activate: activateItem,
                                deactivate: deactivateItem
                            };
                        },
                        clickHandler: ({isActive}) => {
                            this._tagsClick(isActive);
                        },
                        customSubmenu: () => (
                            <TagsSelector
                                numbersTagsTitle={this._translate('REACT.FLOATING_TOOLBAR.TAGS_NUMBER_TITLE')}
                                textTagsTitle={this._translate('REACT.FLOATING_TOOLBAR.TAGS_TEXT_TITLE')}
                                customTagTitle={this._translate('REACT.FLOATING_TOOLBAR.TAGS_CUSTOM_TITLE')}
                                onSelectNumTag={(tag) => {
                                    this._labelClick(CANVAS_TOOLS.NUMTAG, tag);
                                }}
                                onSelectTextTag={(tag) => {
                                    this._labelClick(CANVAS_TOOLS.TEXTTAG, tag);
                                }}
                                textTags={this._accountData.dashboardTags}
                            />
                        )
                    }
                ]
            },
            // Group 8
            {
                groupId: 'Group8',
                available:
                    !this._dashboardState.isObserve &&
                    (this._dashboardState.isCanvas || this._dashboardState.isImageScan) &&
                    this._dashboardState.hideWifiScan,
                items: [
                    {
                        title: this._translate('REACT.FLOATING_TOOLBAR.UNDO'),
                        icon: 'undo',
                        disabled:
                            this._dashboardState.isScanMode ||
                            !this._dashboardState.isAnnotated ||
                            this._dashboardState.canvasReadOnly,
                        clickHandler: () => {
                            this._tsCanvasAnnotate.undo();
                        }
                    }
                ]
            }
        ];

        return groups.filter((group) => !!group.available);
    }

    private _isScanDisabled() {
        if (this._dashboardState.isLiveVideo) {
            return false;
        }

        if (this._dashboardState.isDesktopSharingSession) {
            return false;
        }

        if (
            (this._dashboardState.isScreenSharing || this._dashboardState.isApplicationSharing) &&
            this._dashboardState.videoModeState.videoState.focus
        ) {
            return false;
        }

        return (
            (this._dashboardState.displayLoaderReconnecting && !this._dashboardState.showImageWidget) ||
            !((this._dashboardState.isCanvas && this._dashboardState.canvasReady) || this._dashboardState.isScanMode)
        );
    }

    private _isZoomDisabled() {
        return (
            !this._dashboardState.canvasReady ||
            this._dashboardState.isImageScan ||
            !this._tsCanvasAnnotate.zoomEnabled ||
            this._dashboardState.canvasReadOnly
        );
    }

    private _isMoveDisabled() {
        return (
            !this._tsCanvasAnnotate.canvasReady ||
            this._dashboardState.isImageScan ||
            this._dashboardState.canvasReadOnly
        );
    }

    private _isRotateDisabled() {
        return (
            !this._dashboardState.canvasReady ||
            this._dashboardState.isImageScan ||
            !this._tsCanvasAnnotate.rightRotateEnabled ||
            this._dashboardState.canvasReadOnly
        );
    }

    private _isDrawDisabled() {
        return (
            !this._dashboardState.canvasReady || this._dashboardState.isImageScan || this._dashboardState.canvasReadOnly
        );
    }

    private _deactivateTools() {
        Object.keys(this._canvasToolsHandlers).forEach((tool) => {
            if (!(tool === TOOL_HANDLER_KEYS.ZOOM && this._tsCanvasAnnotate.getZoom() > 1)) {
                this._canvasToolsHandlers[tool as TOOL_HANDLER_KEYS].deactivate();
            }
        });
    }

    private _moveClick(isActive: boolean) {
        this._deactivateTools();

        if (isActive) {
            this._canvasToolsHandlers.move.activate();
            this._setTool(CANVAS_TOOLS.HAND);
        } else {
            this._setDefaultTool();
        }
    }

    private _shapeClick(tool: CANVAS_TOOLS, itemInfo: IClickItemInfo & {item: IFloatingToolbarItem}) {
        this._deactivateTools();
        this._canvasToolsHandlers.shape.activate();

        if (itemInfo.parent) {
            itemInfo.parent.activateItem();
            itemInfo.parent.updateIcon(itemInfo.item.icon);
        }

        this._lastShape = {
            tool: tool,
            icon: itemInfo.item.icon
        };

        this._setTool(tool);
    }

    private _textClick(isActive: boolean) {
        this._deactivateTools();

        if (!isActive) {
            this._canvasToolsHandlers.text.activate();
            this._setTool(CANVAS_TOOLS.TEXT);
        } else {
            this._setDefaultTool();
        }
    }

    private _tagsClick(isActive: boolean) {
        if (isActive) {
            this._setDefaultTool();
        }
    }

    private _labelClick(type: CANVAS_TOOLS.NUMTAG | CANVAS_TOOLS.TEXTTAG, tag?: string) {
        this._deactivateTools();
        this._canvasToolsHandlers.labels.activate();

        this._setTool(type, tag);
    }

    private _zoomChanged(value: number) {
        this._deactivateTools();

        if (value > 1) {
            this._canvasToolsHandlers.zoom.activate();
        } else {
            this._canvasToolsHandlers.zoom.deactivate();
        }

        this._canvasToolsHandlers.move.activate();
        this._tsCanvasAnnotate.setZoom(Number(value));
        this._tsCanvasAnnotate.setTool(CANVAS_TOOLS.HAND);
    }

    private _updateColor(color: string, props: ICustomSubmenuProps) {
        props.updateIconClassName(color.toLowerCase().indexOf('#ffffff') !== -1 ? 'backlayer' : '');
        props.updateStyles({icon: {color: color}});
        this._tsCanvasAnnotate.setColor(color);
    }

    private _setTool(tool: CANVAS_TOOLS | null, options?: string) {
        this._tsCanvasAnnotate.setTool(tool, options);
    }

    private _setDefaultTool() {
        this._deactivateTools();

        if (this._canvasToolsHandlers.shape.updateIcon) {
            this._canvasToolsHandlers.shape.updateIcon(this._lastShape.icon);
        }

        this._canvasToolsHandlers.shape.activate();

        this._tsCanvasAnnotate.setTool(this._lastShape.tool);
    }

    _speedtestClickHandler(activateItem: Function, deactivateItem: Function, isActive: boolean) {
        if (!isActive) {
            this.emit(ToolbarEventType.run_speedtest);
            activateItem();

            const roomId = this._dashboardState.sessionInfo.sessionRoomId;
            const date = new Date();

            this._eventsLogService.debug({
                roomId,
                logType: LOG_EVENTS.speedtestRequested,
                meta: {date}
            });

            this._roomService.setReportedField(roomId, {
                key: 'speedtestRequestTimestamp',
                value: date
            });
        } else {
            deactivateItem();
            this._setDefaultTool();
        }
    }

    @computed
    get recordingStatusMessage() {
        switch (this._dashboardState.dashboardRoomStorage.recordingStatus) {
            case RECORDING_STATUS.ready:
            case RECORDING_STATUS.failed:
                return this._translate('REACT.FLOATING_TOOLBAR.RECORD.START');
            case RECORDING_STATUS.started:
                return this._translate('REACT.FLOATING_TOOLBAR.RECORD.STOP');
            case RECORDING_STATUS.stopping:
                return this._translate('REACT.FLOATING_TOOLBAR.RECORD.SAVING');
            case RECORDING_STATUS.starting:
                return this._translate('REACT.FLOATING_TOOLBAR.RECORD.STARTING');
            default:
                return '';
        }
    }

    @computed
    private get _isVisualScanRunning(): boolean {
        return this._dashboardState.floatingToolbarState.isStopVisualWifiScan;
    }

    @computed
    private get _isWifiScanRunning(): boolean {
        return get(this._dashboardState, 'isMobileWifiScanInProgress', false);
    }

    @computed
    private get _isOCRScanRunning(): boolean {
        return this._dashboardState.isScanMode;
    }

    @computed
    private get _isVisualScanPendingMobileApproval(): boolean {
        return this._dashboardState.isVisualScanPendingMobileApproval;
    }

    @computed
    private get _isWifiScanButtonDisabled(): boolean {
        return (
            this._dashboardState.floatingToolbarState.isRunWifiButtonDisabled ||
            this._isVisualScanRunning ||
            this._isOCRScanRunning ||
            this._isVisualScanPendingMobileApproval
        );
    }

    @computed
    private get _isOCRScanButtonDisabled(): boolean {
        return (
            this._isScanDisabled() ||
            this._isWifiScanRunning ||
            this._isVisualScanRunning ||
            this._isVisualScanPendingMobileApproval
        );
    }

    @computed
    get getWifiScanTooltipContent() {
        if (this._isWifiScanRunning) {
            return this._translate('REACT.FLOATING_TOOLBAR.WIFI_TOOLTIP.WIFI_SCAN_IN_PROGRESS');
        }

        if (this._isVisualScanRunning || this._dashboardState.isVisualScanPendingMobileApproval) {
            return this._translate('REACT.FLOATING_TOOLBAR.WIFI_TOOLTIP.VISUAL_SCAN_IN_PROGRESS');
        }

        if (this._isOCRScanRunning) {
            return this._translate('REACT.FLOATING_TOOLBAR.WIFI_TOOLTIP.OCR_SCAN_IN_PROGRESS');
        }

        return '';
    }

    @computed
    get getVisualScanTooltipContent() {
        const isCapable = get(this._dashboardState, 'isDeviceSupportsVisualScan', true);
        const isWifiScanInProgress = get(this._dashboardState, 'isMobileWifiScanInProgress', '');
        const isOCRProcessing = this._dashboardState.isScanMode;
        const isVisualScanPendingMobileApproval = this._dashboardState.isVisualScanPendingMobileApproval;

        if (!isCapable) {
            return this._translate('REACT.FLOATING_TOOLBAR.WIFI_VISUAL_SCAN_TOOLTIP.NOT_SUPPORTED');
        }

        if (isWifiScanInProgress) {
            return this._translate('REACT.FLOATING_TOOLBAR.VISUAL_TOOLTIP.WIFI_SCAN_IN_PROGRESS');
        }

        if (isOCRProcessing) {
            return this._translate('REACT.FLOATING_TOOLBAR.VISUAL_TOOLTIP.OCR_SCAN_IN_PROGRESS');
        }

        if (isVisualScanPendingMobileApproval) {
            return this._translate('REACT.FLOATING_TOOLBAR.WIFI_VISUAL_SCAN_TOOLTIP.WIFI_SCAN_WAITING_FOR_APPROVAL');
        }

        return '';
    }

    @computed
    get getOCRScanTooltipContent() {
        if (this._isWifiScanRunning) {
            return this._translate('REACT.FLOATING_TOOLBAR.OCR_TOOLTIP.WIFI_SCAN_IN_PROGRESS');
        }

        if (this._isVisualScanRunning || this._dashboardState.isVisualScanPendingMobileApproval) {
            return this._translate('REACT.FLOATING_TOOLBAR.OCR_TOOLTIP.VISUAL_SCAN_IN_PROGRESS');
        }

        return '';
    }

    _wifiScanClickHandler() {
        this.emit(ToolbarEventType.run_wifi_scan);
        const roomId = this._dashboardState.sessionInfo.sessionRoomId;
        const date = new Date();

        this._eventsLogService.debug({
            roomId,
            logType: LOG_EVENTS.wifiScanInitiated,
            meta: {date}
        });

        this._roomService.setReportedField(roomId, {
            key: 'wifiScanRequestTimestamp',
            value: date
        });
    }

    _wifiVisualScanClickHandler() {
        this.emit(ToolbarEventType.start_wifi_visual_scan);
    }
}
